import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { productCategories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50'
          : 'bg-black/25 backdrop-blur-sm'
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-10 py-3 md:py-4">
        {/* Logo */}
        <Link
          to="/"
          className={`font-heading font-bold text-lg md:text-xl whitespace-nowrap transition-colors ${
            scrolled ? 'text-foreground-950' : 'text-white'
          }`}
        >
          AllChinaBuy<span className="font-normal">Sheet</span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
            }`}
          >
            Home
          </Link>
          <a
            href="#categories"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
            }`}
          >
            Categories
          </a>
          <Link
            to="/article/how-to-use-allchinabuy-spreadsheet"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
            }`}
          >
            Shopping Guide
          </Link>
          <a
            href="#articles"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
            }`}
          >
            Guides
          </a>
          <a
            href="#faq"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
            }`}
          >
            FAQ
          </a>

          {/* Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                scrolled
                  ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  : 'bg-white/12 text-white hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              Browse
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-background-200 overflow-hidden max-h-[420px] overflow-y-auto z-50">
                {productCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
              scrolled
                ? 'bg-primary-500 text-white hover:bg-primary-600'
                : 'bg-white text-foreground-950 hover:bg-white/90'
            }`}
          >
            allchinabuy sheet
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
              scrolled
                ? 'bg-accent-500 text-foreground-950 hover:bg-accent-400'
                : 'bg-white/15 backdrop-blur-sm text-white border border-white/25 hover:bg-white/25'
            }`}
          >
            buy it now
          </a>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line mr-2"></i>
              Home
            </Link>
            <a
              href="#categories"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-grid-line mr-2"></i>
              Categories
            </a>
            <Link
              to="/article/how-to-use-allchinabuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line mr-2"></i>
              Shopping Guide
            </Link>
            <a
              href="#articles"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-article-line mr-2"></i>
              Guides
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-question-line mr-2"></i>
              FAQ
            </a>

            <div className="border-t border-background-200 my-3"></div>

            <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              Product Categories
            </p>
            <div className="grid grid-cols-2 gap-1">
              {productCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-7 h-7 rounded-md bg-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 my-3"></div>

            <div className="flex gap-2 px-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                allchinabuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-accent-500 text-foreground-950 text-sm font-semibold text-center hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}